import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Upload, X, ImagePlus, CopyPlus, Star, ChevronLeft, ChevronRight, ArrowUp, ArrowDown } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { fmtBRL } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const EMPTY = {
  name: "", description: "", category_id: "", price: "", promo_price: "",
  price_card: "", max_installments: 12,
  sku: "", manufacturer: "", model: "",
  width: "", height: "", depth: "", materials: "",
  color_variants: [], extra_category_ids: [], warranty_text: "", assembly_text: "", delivery_text: "", important_notes: "", order: 0,
  images: [], sizes: [], stock: 0, featured: false, active: true,
  seo_title: "", seo_description: "", discount_badge: true, discount_percent: 0,
};

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [newColor, setNewColor] = useState("");
  const [sizesText, setSizesText] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [variantUrls, setVariantUrls] = useState({});
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);

  const load = () => {
    api.get("/admin/products").then((r) => setProducts(r.data)).finally(() => setLoading(false));
    api.get("/admin/categories").then((r) => setCategories(r.data)).catch(() => {});
  };
  useEffect(load, []);

  // ordenação manual: a lista do painel espelha a ordem das vitrines
  const orderedProducts = [...products].sort(
    (a, b) => (a.manual_order || 0) === 0 && (b.manual_order || 0) === 0
      ? a.name.localeCompare(b.name)
      : (a.manual_order || 9999) - (b.manual_order || 9999)
  );

  const moveProduct = async (index, dir) => {
    const next = [...orderedProducts];
    const j = index + dir;
    if (j < 0 || j >= next.length) return;
    [next[index], next[j]] = [next[j], next[index]];
    const ids = next.map((p) => p.id);
    setProducts(next.map((p, i) => ({ ...p, manual_order: i + 1 })));
    try {
      await api.put("/admin/products-reorder", { ids });
      toast.success("Ordem das vitrines atualizada!");
    } catch (err) {
      toast.error(formatApiError(err));
      load();
    }
  };

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY);
    setNewColor("");
    setSizesText("");
    setImageUrl("");
    setDialogOpen(true);
  };

  const openEdit = (p) => {
    setEditing(p);
    setForm({
      name: p.name, description: p.description, category_id: p.category_id,
      price: p.price, promo_price: p.promo_price ?? "", price_card: p.price_card ?? "",
      max_installments: p.max_installments || 12,
      sku: p.sku || "", manufacturer: p.manufacturer || "", model: p.model || "",
      width: p.width || "", height: p.height || "", depth: p.depth || "", materials: p.materials || "",
      color_variants: (p.color_variants?.length
        ? p.color_variants
        : (p.colors || []).map((name) => ({ name }))
      ).map((v) => ({ images: [], main_image: "", available: true, ...v })),
      extra_category_ids: p.extra_category_ids || [],
      warranty_text: p.warranty_text || "", assembly_text: p.assembly_text || "",
      delivery_text: p.delivery_text || "", important_notes: p.important_notes || "", order: p.order || 0,
      images: p.images || [],
      stock: p.stock, featured: p.featured, active: p.active,
      seo_title: p.seo_title || "", seo_description: p.seo_description || "",
      discount_badge: p.discount_badge !== false, discount_percent: p.discount_percent || 0,
    });
    setNewColor("");
    setVariantUrls({});
    setSizesText((p.sizes || []).join(", "));
    setImageUrl("");
    setDialogOpen(true);
  };

  const addColor = () => {
    const c = newColor.trim();
    if (!c) return;
    if ((form.color_variants || []).some((x) => x.name.toLowerCase() === c.toLowerCase())) {
      toast.error("Essa cor já está na lista.");
      return;
    }
    setForm((f) => ({
      ...f,
      color_variants: [...(f.color_variants || []), { name: c, images: [], main_image: "", available: true }],
    }));
    setNewColor("");
  };

  const removeColor = (i) => {
    setForm((f) => ({
      ...f,
      color_variants: (f.color_variants || []).filter((_, idx) => idx !== i),
    }));
  };

  // ---- galeria por cor
  const setVariant = (vi, patch) =>
    setForm((f) => ({
      ...f,
      color_variants: (f.color_variants || []).map((v, i) => (i === vi ? { ...v, ...patch } : v)),
    }));

  const variantAddUrl = (vi) => {
    const url = (variantUrls[vi] || "").trim();
    if (!url) return;
    const v = form.color_variants[vi];
    setVariant(vi, { images: [...v.images, url], main_image: v.main_image || url });
    setVariantUrls((s) => ({ ...s, [vi]: "" }));
  };

  const variantRemoveImage = (vi, ii) => {
    const v = form.color_variants[vi];
    const removed = v.images[ii];
    const images = v.images.filter((_, i) => i !== ii);
    setVariant(vi, { images, main_image: v.main_image === removed ? images[0] || "" : v.main_image });
  };

  const variantMoveImage = (vi, ii, dir) => {
    const v = form.color_variants[vi];
    const j = ii + dir;
    if (j < 0 || j >= v.images.length) return;
    const images = [...v.images];
    [images[ii], images[j]] = [images[j], images[ii]];
    setVariant(vi, { images });
  };

  const uploadFiles = async (files, variantIndex = null) => {
    for (const file of files) {
      const fd = new FormData();
      fd.append("file", file);
      try {
        const { data } = await api.post("/admin/upload", fd);
        if (variantIndex === null) {
          setForm((f) => ({ ...f, images: [...f.images, data.url] }));
        } else {
          setForm((f) => ({
            ...f,
            color_variants: f.color_variants.map((v, i) =>
              i === variantIndex
                ? { ...v, images: [...v.images, data.url], main_image: v.main_image || data.url }
                : v
            ),
          }));
        }
      } catch (err) {
        toast.error(formatApiError(err, "Falha no upload da imagem."));
      }
    }
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.category_id || !form.price) {
      toast.error("Preencha nome, categoria e preço.");
      return;
    }
    setSaving(true);
    const payload = {
      ...form,
      price: Number(form.price),
      promo_price: form.promo_price ? Number(form.promo_price) : null,
      price_card: form.price_card ? Number(form.price_card) : null,
      max_installments: Number(form.max_installments) || 12,
      order: Number(form.order) || 0,
      stock: Number(form.stock) || 0,
      color_variants: (form.color_variants || []).map((v) => ({
        name: v.name,
        images: v.images || [],
        main_image: v.main_image || "",
        available: v.available !== false,
      })),
      extra_category_ids: (form.extra_category_ids || []).filter((id) => id !== form.category_id),
      sizes: sizesText.split(",").map((s) => s.trim()).filter(Boolean),
    };
    try {
      if (editing) {
        await api.put(`/admin/products/${editing.id}`, payload);
        toast.success("Produto atualizado!");
      } else {
        await api.post("/admin/products", payload);
        toast.success("Produto criado!");
      }
      setDialogOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err, "Não foi possível salvar."));
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/admin/products/${deleting.id}`);
      toast.success("Produto excluído.");
      setDeleting(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err, "Não foi possível excluir."));
    }
  };

  const duplicate = async (p) => {
    try {
      await api.post("/admin/products", {
        name: `${p.name.replace(/\s*\(Cópia\)\s*$/, "")} (Cópia)`,
        description: p.description,
        category_id: p.category_id,
        price: p.price,
        promo_price: p.promo_price ?? null,
        price_card: p.price_card ?? null,
        max_installments: p.max_installments || 12,
        sku: p.sku ? `${p.sku}-COPIA` : "",
        manufacturer: p.manufacturer || "",
        model: p.model || "",
        width: p.width || "", height: p.height || "", depth: p.depth || "",
        materials: p.materials || "",
        color_variants: p.color_variants || [],
        extra_category_ids: p.extra_category_ids || [],
        warranty_text: p.warranty_text || "", assembly_text: p.assembly_text || "",
        delivery_text: p.delivery_text || "", important_notes: p.important_notes || "", order: p.order || 0,
        images: p.images || [],
        sizes: p.sizes || [],
        stock: p.stock,
        featured: false,
        active: false,
        seo_title: p.seo_title || "", seo_description: p.seo_description || "",
      });
      toast.success("Produto duplicado como rascunho (inativo). Edite e ative quando quiser.");
      load();
    } catch (err) {
      toast.error(formatApiError(err, "Não foi possível duplicar."));
    }
  };

  return (
    <div data-testid="admin-products">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-heading text-2xl font-bold text-gray-900">Produtos</h1>
          <p className="text-sm text-gray-500 mt-1">{products.length} produto(s) cadastrado(s)</p>
        </div>
        <Button onClick={openCreate} className="rounded-full" data-testid="product-create-button">
          <Plus className="w-4 h-4" /> Novo Produto
        </Button>
      </div>

      <div className="mt-6 bg-white border border-slate-200 rounded-xl overflow-x-auto">
        <table className="w-full text-sm min-w-[720px]">
          <thead>
            <tr className="border-b border-slate-100 text-left text-xs uppercase tracking-wide text-slate-500">
              <th className="p-4">Produto</th>
              <th className="p-4">Categoria</th>
              <th className="p-4">Preço</th>
              <th className="p-4">Estoque</th>
              <th className="p-4">Status</th>
              <th className="p-4 text-right">Ações</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="p-8 text-center text-slate-400">Carregando...</td></tr>
            ) : products.length === 0 ? (
              <tr><td colSpan={6} className="p-8 text-center text-slate-400">Nenhum produto cadastrado.</td></tr>
            ) : (
              orderedProducts.map((p, idx) => (
                <tr key={p.id} className="border-b border-slate-50 hover:bg-slate-50/60" data-testid={`admin-product-row-${p.id}`}>
                  <td className="p-4">
                    <div className="flex items-center gap-3">
                      <img src={p.images?.[0] || "/assets/logo.png"} alt="" className="w-11 h-11 rounded-lg object-cover bg-slate-100" />
                      <div className="min-w-0">
                        <p className="font-medium text-gray-800 truncate max-w-[260px]">{p.name}</p>
                        {p.is_demo && <span className="text-[10px] text-slate-400">demonstração</span>}
                      </div>
                    </div>
                  </td>
                  <td className="p-4 text-slate-600">{p.category?.name || "—"}</td>
                  <td className="p-4">
                    <span className="font-semibold text-gray-900">{fmtBRL(p.effective_price)}</span>
                    {p.promo_price && p.promo_price < p.price && (
                      <span className="block text-xs text-slate-400 line-through">{fmtBRL(p.price)}</span>
                    )}
                  </td>
                  <td className="p-4">
                    <span className={p.stock <= 3 ? "text-red-600 font-semibold" : "text-slate-600"}>
                      {p.stock} un.
                    </span>
                  </td>
                  <td className="p-4">
                    <span className={`text-xs font-semibold rounded-full px-2.5 py-1 ${p.active ? "bg-green-50 text-green-700" : "bg-slate-100 text-slate-500"}`}>
                      {p.active ? "Ativo" : "Inativo"}
                    </span>
                  </td>
                  <td className="p-4">
                    <div className="flex justify-end gap-1">
                      <Button variant="ghost" size="icon" title="Mover para cima nas vitrines" onClick={() => moveProduct(idx, -1)} disabled={idx === 0} data-testid={`product-up-${p.id}`}>
                        <ArrowUp className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="icon" title="Mover para baixo nas vitrines" onClick={() => moveProduct(idx, 1)} disabled={idx === orderedProducts.length - 1} data-testid={`product-down-${p.id}`}>
                        <ArrowDown className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="icon" title="Duplicar produto" onClick={() => duplicate(p)} data-testid={`product-duplicate-${p.id}`}>
                        <CopyPlus className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="icon" title="Editar produto" onClick={() => openEdit(p)} data-testid={`product-edit-${p.id}`}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="icon" title="Excluir produto" className="text-red-500 hover:text-red-600" onClick={() => setDeleting(p)} data-testid={`product-delete-${p.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* dialog de criação/edição */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="product-form-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Editar Produto" : "Novo Produto"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4 mt-2">
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <Label>Nome do produto *</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" required data-testid="product-form-name" />
              </div>
              <div>
                <Label>Categoria *</Label>
                <Select value={form.category_id} onValueChange={(v) => setForm({ ...form, category_id: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="product-form-category">
                    <SelectValue placeholder="Selecione" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories
                      .filter((c) => !c.parent_id)
                      .flatMap((root) => [
                        root,
                        ...categories.filter((c) => c.parent_id === root.id),
                      ])
                      .map((c) => (
                        <SelectItem key={c.id} value={c.id} data-testid={`product-form-cat-option-${c.slug || c.id}`}>
                          {c.parent_id
                            ? `${categories.find((r) => r.id === c.parent_id)?.name || ""} › ${c.name}`
                            : c.name}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
                {/* onde o produto será exibido */}
                {form.category_id && (
                  <p className="mt-1.5 text-xs text-slate-500" data-testid="product-form-exibido-em">
                    Será exibido em:{" "}
                    <strong className="text-brand">
                      {(() => {
                        const sel = categories.find((c) => c.id === form.category_id);
                        if (!sel) return "—";
                        if (sel.parent_id) {
                          const pai = categories.find((r) => r.id === sel.parent_id);
                          return `${pai?.name || ""} › ${sel.name} (e também em ${pai?.name || "categoria principal"})`;
                        }
                        return sel.name;
                      })()}
                    </strong>
                  </p>
                )}
              </div>
              <div className="sm:col-span-2">
                <Label>Vitrines extras (opcional) — o produto também aparece nestas categorias</Label>
                <div className="mt-1.5 flex flex-wrap gap-2">
                  {categories.filter((c) => c.id !== form.category_id).map((c) => {
                    const on = (form.extra_category_ids || []).includes(c.id);
                    const label = c.parent_id
                      ? `${categories.find((r) => r.id === c.parent_id)?.name || ""} › ${c.name}`
                      : c.name;
                    return (
                      <button
                        type="button"
                        key={c.id}
                        onClick={() =>
                          setForm((f) => ({
                            ...f,
                            extra_category_ids: on
                              ? (f.extra_category_ids || []).filter((id) => id !== c.id)
                              : [...(f.extra_category_ids || []), c.id],
                          }))
                        }
                        className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                          on
                            ? "bg-brand text-white border-brand"
                            : "bg-white text-gray-600 border-slate-200 hover:border-brand/50"
                        }`}
                        data-testid={`extra-cat-${c.slug || c.id}`}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div>
                <Label>Estoque</Label>
                <Input type="number" min="0" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} className="mt-1.5" data-testid="product-form-stock" />
              </div>
              <div>
                <Label>Preço à vista — Pix/dinheiro (R$) *</Label>
                <Input type="number" step="0.01" min="0" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="mt-1.5" required data-testid="product-form-price" />
              </div>
              <div>
                <Label>Promoção à vista (R$) — opcional</Label>
                <Input type="number" step="0.01" min="0" value={form.promo_price} onChange={(e) => setForm({ ...form, promo_price: e.target.value })} className="mt-1.5" placeholder="Preço à vista promocional" data-testid="product-form-promo" />
              </div>
              <div>
                <Label>Preço no cartão de crédito (R$)</Label>
                <Input type="number" step="0.01" min="0" value={form.price_card} onChange={(e) => setForm({ ...form, price_card: e.target.value })} className="mt-1.5" placeholder="Vazio = mesmo do preço à vista" data-testid="product-form-price-card" />
                <p className="text-[10px] text-slate-400 mt-1">Valor total informado por você — sem cálculo automático.</p>
              </div>
              <div className="sm:col-span-2 border border-amber-100 bg-amber-50/50 rounded-lg p-3.5">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold text-gray-800">Selo de desconto sobre a foto</p>
                    <p className="text-[11px] text-slate-500 mt-0.5">
                      Só aparece quando existe promoção real (promoção à vista menor que o preço à vista).
                      A porcentagem é calculada do preço real, a menos que você informe uma manualmente.
                    </p>
                  </div>
                  <Switch
                    checked={form.discount_badge !== false}
                    onCheckedChange={(v) => setForm({ ...form, discount_badge: v })}
                    data-testid="product-form-discount-badge"
                  />
                </div>
                {form.discount_badge !== false && (
                  <div className="mt-2.5 max-w-[220px]">
                    <Label>Porcentagem manual (opcional)</Label>
                    <Input
                      type="number" min="1" max="90"
                      value={form.discount_percent || ""}
                      onChange={(e) => setForm({ ...form, discount_percent: e.target.value ? Number(e.target.value) : 0 })}
                      placeholder="Vazio = calcula do preço real"
                      className="mt-1.5"
                      data-testid="product-form-discount-percent"
                    />
                  </div>
                )}
              </div>
              <div>
                <Label>Máximo de parcelas (1 a 12)</Label>
                <Select value={String(form.max_installments)} onValueChange={(v) => setForm({ ...form, max_installments: Number(v) })}>
                  <SelectTrigger className="mt-1.5" data-testid="product-form-installments">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Array.from({ length: 12 }, (_, i) => i + 1).map((n) => (
                      <SelectItem key={n} value={String(n)}>{n}x</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Código / SKU</Label>
                <Input value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} className="mt-1.5" placeholder="Ex: SOF-001" data-testid="product-form-sku" />
              </div>
              <div>
                <Label>Ordem de exibição</Label>
                <Input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} className="mt-1.5" data-testid="product-form-order" />
              </div>
              <div>
                <Label>Fabricante</Label>
                <Input value={form.manufacturer} onChange={(e) => setForm({ ...form, manufacturer: e.target.value })} className="mt-1.5" data-testid="product-form-manufacturer" />
              </div>
              <div>
                <Label>Modelo</Label>
                <Input value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} className="mt-1.5" data-testid="product-form-model" />
              </div>
              <div className="sm:col-span-2 grid grid-cols-3 gap-3">
                <div>
                  <Label>Altura</Label>
                  <Input value={form.height} onChange={(e) => setForm({ ...form, height: e.target.value })} placeholder="0,85 m" className="mt-1.5" data-testid="product-form-height" />
                </div>
                <div>
                  <Label>Largura</Label>
                  <Input value={form.width} onChange={(e) => setForm({ ...form, width: e.target.value })} placeholder="2,00 m" className="mt-1.5" data-testid="product-form-width" />
                </div>
                <div>
                  <Label>Profundidade</Label>
                  <Input value={form.depth} onChange={(e) => setForm({ ...form, depth: e.target.value })} placeholder="0,95 m" className="mt-1.5" data-testid="product-form-depth" />
                </div>
              </div>
              <div className="sm:col-span-2 border border-slate-100 rounded-lg p-3.5 bg-slate-50/50">
                <p className="text-xs font-bold uppercase tracking-wide text-slate-500">SEO (Google) — opcional</p>
                <div className="mt-2 space-y-3">
                  <div>
                    <Label>Título na pesquisa do Google</Label>
                    <Input
                      value={form.seo_title}
                      onChange={(e) => setForm({ ...form, seo_title: e.target.value })}
                      placeholder="Vazio = nome do produto + HG Móveis"
                      maxLength={70}
                      className="mt-1.5"
                      data-testid="product-form-seo-title"
                    />
                    <p className="text-[11px] text-slate-400 mt-1">{form.seo_title.length}/70 caracteres</p>
                  </div>
                  <div>
                    <Label>Descrição na pesquisa do Google</Label>
                    <Textarea
                      rows={2}
                      value={form.seo_description}
                      onChange={(e) => setForm({ ...form, seo_description: e.target.value })}
                      placeholder="Vazio = gerada automaticamente com nome e preço"
                      maxLength={160}
                      className="mt-1.5 text-sm"
                      data-testid="product-form-seo-description"
                    />
                    <p className="text-[11px] text-slate-400 mt-1">{form.seo_description.length}/160 caracteres</p>
                  </div>
                </div>
              </div>
              <div className="sm:col-span-2">
                <Label>Materiais e características</Label>
                <Input value={form.materials} onChange={(e) => setForm({ ...form, materials: e.target.value })} className="mt-1.5" placeholder="Ex: estrutura em madeira maciça, tecido suede" data-testid="product-form-materials" />
              </div>
              <div className="sm:col-span-2">
                <Label>Descrição</Label>
                <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} className="mt-1.5" data-testid="product-form-description" />
              </div>
              <div className="sm:col-span-2">
                <Label>Cores disponíveis</Label>
                <div className="mt-1.5 flex flex-wrap items-center gap-2">
                  {(form.color_variants || []).map((v, i) => (
                    <span
                      key={v.name}
                      className="inline-flex items-center gap-1 bg-brand-light text-brand rounded-full pl-3 pr-1.5 py-1 text-sm font-medium"
                      data-testid={`color-chip-${i}`}
                    >
                      {v.name}
                      <button
                        type="button"
                        onClick={() => removeColor(i)}
                        className="p-0.5 rounded-full hover:bg-brand/20 transition-colors"
                        aria-label={`Remover cor ${v.name}`}
                        data-testid={`color-remove-${i}`}
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </span>
                  ))}
                  {(form.color_variants || []).length === 0 && (
                    <span className="text-xs text-slate-400">Nenhuma cor cadastrada — adicione abaixo.</span>
                  )}
                </div>
                <div className="mt-2 flex gap-2">
                  <Input
                    value={newColor}
                    onChange={(e) => setNewColor(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        addColor();
                      }
                    }}
                    placeholder="Digite a cor — ex: Preto, Cinza, Marrom"
                    className="h-9 text-sm"
                    data-testid="color-new-input"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={addColor}
                    disabled={!newColor.trim()}
                    className="shrink-0"
                    data-testid="color-add-button"
                  >
                    <Plus className="w-4 h-4" /> Adicionar cor
                  </Button>
                </div>
              </div>
              <div className="sm:col-span-2">
                <Label>Medidas (separadas por vírgula)</Label>
                <Input value={sizesText} onChange={(e) => setSizesText(e.target.value)} placeholder="1,80m x 2,00m" className="mt-1.5" data-testid="product-form-sizes" />
              </div>
            </div>

            {/* imagens */}
            <div>
              <Label>Fotos do produto</Label>
              <div className="mt-1.5 flex flex-wrap gap-2">
                {form.images.map((img, i) => (
                  <div key={i} className="relative group">
                    <img src={img} alt="" className="w-20 h-20 rounded-lg object-cover border border-slate-200" />
                    <button
                      type="button"
                      onClick={() => setForm((f) => ({ ...f, images: f.images.filter((_, idx) => idx !== i) }))}
                      className="absolute -top-1.5 -right-1.5 bg-red-500 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                      data-testid={`product-image-remove-${i}`}
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
                <label className="w-20 h-20 rounded-lg border-2 border-dashed border-slate-300 flex flex-col items-center justify-center text-slate-400 hover:border-brand hover:text-brand cursor-pointer transition-colors" data-testid="product-image-upload">
                  <Upload className="w-5 h-5" />
                  <span className="text-[9px] mt-0.5">Upload</span>
                  <input type="file" accept="image/*" multiple className="hidden" onChange={(e) => uploadFiles([...e.target.files])} />
                </label>
              </div>
              <div className="mt-2 flex gap-2">
                <Input value={imageUrl} onChange={(e) => setImageUrl(e.target.value)} placeholder="ou cole a URL de uma imagem" className="h-9 text-sm" data-testid="product-image-url" />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    if (imageUrl.trim()) {
                      setForm((f) => ({ ...f, images: [...f.images, imageUrl.trim()] }));
                      setImageUrl("");
                    }
                  }}
                  data-testid="product-image-add-url"
                >
                  <ImagePlus className="w-4 h-4" /> Adicionar
                </Button>
              </div>
            </div>

            {/* galeria por cor */}
            {(form.color_variants || []).length > 0 && (
              <div>
                <Label>Fotos de cada cor (a galeria muda quando o cliente escolhe a cor)</Label>
                <div className="mt-1.5 space-y-3">
                  {(form.color_variants || []).map((v, vi) => (
                    <div key={v.name} className="border border-slate-200 rounded-xl p-3 space-y-2.5" data-testid={`variant-block-${vi}`}>
                      <div className="flex items-center justify-between gap-2 flex-wrap">
                        <p className="text-sm font-semibold text-gray-800">
                          {v.name}
                          {v.images.length === 0 && (
                            <span className="ml-2 text-[11px] font-medium text-amber-600">
                              sem fotos próprias — usa a galeria geral
                            </span>
                          )}
                        </p>
                        <label className="flex items-center gap-2 text-xs text-gray-600">
                          <Switch
                            checked={v.available !== false}
                            onCheckedChange={(val) => setVariant(vi, { available: val })}
                            data-testid={`variant-available-${vi}`}
                          />
                          {v.available !== false ? "Disponível" : "Esgotada"}
                        </label>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {v.images.map((img, ii) => (
                          <div key={ii} className="relative group">
                            <img
                              src={img}
                              alt=""
                              className={`w-20 h-20 rounded-lg object-cover border-2 ${img === v.main_image ? "border-yellow-400" : "border-slate-200"}`}
                            />
                            {img === v.main_image && (
                              <span className="absolute -top-1.5 -left-1.5 bg-yellow-400 text-slate-900 rounded-full p-1 shadow" title="Foto principal da cor">
                                <Star className="w-3 h-3 fill-current" />
                              </span>
                            )}
                            <div className="absolute inset-0 rounded-lg bg-black/55 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1.5">
                              <button type="button" title="Definir como foto principal" onClick={() => setVariant(vi, { main_image: img })} className="text-yellow-400 hover:scale-110 transition-transform" data-testid={`variant-main-${vi}-${ii}`}>
                                <Star className="w-4 h-4" />
                              </button>
                              <button type="button" title="Mover para a esquerda" onClick={() => variantMoveImage(vi, ii, -1)} disabled={ii === 0} className="text-white hover:scale-110 transition-transform disabled:opacity-30" data-testid={`variant-left-${vi}-${ii}`}>
                                <ChevronLeft className="w-4 h-4" />
                              </button>
                              <button type="button" title="Mover para a direita" onClick={() => variantMoveImage(vi, ii, 1)} disabled={ii === v.images.length - 1} className="text-white hover:scale-110 transition-transform disabled:opacity-30" data-testid={`variant-right-${vi}-${ii}`}>
                                <ChevronRight className="w-4 h-4" />
                              </button>
                              <button type="button" title="Remover foto" onClick={() => variantRemoveImage(vi, ii)} className="text-red-400 hover:scale-110 transition-transform" data-testid={`variant-img-remove-${vi}-${ii}`}>
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        ))}
                        <label className="w-20 h-20 rounded-lg border-2 border-dashed border-slate-300 flex flex-col items-center justify-center text-slate-400 hover:border-brand hover:text-brand cursor-pointer transition-colors" data-testid={`variant-upload-${vi}`}>
                          <Upload className="w-4 h-4" />
                          <span className="text-[9px] mt-0.5">Fotos</span>
                          <input type="file" accept="image/*" multiple className="hidden" onChange={(e) => uploadFiles([...e.target.files], vi)} />
                        </label>
                      </div>
                      <div className="flex gap-2">
                        <Input
                          value={variantUrls[vi] || ""}
                          onChange={(e) => setVariantUrls((s) => ({ ...s, [vi]: e.target.value }))}
                          placeholder="ou cole a URL de uma foto desta cor"
                          className="h-8 text-xs"
                          data-testid={`variant-url-${vi}`}
                        />
                        <Button type="button" variant="outline" size="sm" className="h-8 text-xs shrink-0" onClick={() => variantAddUrl(vi)} data-testid={`variant-add-url-${vi}`}>
                          <ImagePlus className="w-3.5 h-3.5" /> Adicionar
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* textos comerciais por produto (opcionais) */}
            <div className="grid sm:grid-cols-3 gap-3">
              <div>
                <Label>Garantia (opcional — sobrescreve o texto global)</Label>
                <Textarea rows={2} value={form.warranty_text} onChange={(e) => setForm({ ...form, warranty_text: e.target.value })} className="mt-1.5 text-sm" data-testid="product-form-warranty" />
              </div>
              <div>
                <Label>Montagem (opcional)</Label>
                <Textarea rows={2} value={form.assembly_text} onChange={(e) => setForm({ ...form, assembly_text: e.target.value })} className="mt-1.5 text-sm" data-testid="product-form-assembly" />
              </div>
              <div>
                <Label>Entrega (opcional)</Label>
                <Textarea rows={2} value={form.delivery_text} onChange={(e) => setForm({ ...form, delivery_text: e.target.value })} className="mt-1.5 text-sm" data-testid="product-form-delivery" />
              </div>
            </div>

            {/* observações importantes específicas do produto */}
            <div>
              <Label>Observações importantes deste produto (opcional)</Label>
              <p className="text-xs text-slate-400 mt-0.5">
                Um aviso por linha. São somados aos avisos globais de "Informações importantes" (Site e Textos) — não precisa repetir os avisos gerais aqui.
              </p>
              <Textarea rows={3} value={form.important_notes} onChange={(e) => setForm({ ...form, important_notes: e.target.value })} className="mt-1.5 text-sm" data-testid="product-form-important-notes" />
            </div>

            <div className="flex items-center gap-6 pt-2">
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <Switch checked={form.featured} onCheckedChange={(v) => setForm({ ...form, featured: v })} data-testid="product-form-featured" />
                Destaque na home
              </label>
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="product-form-active" />
                Ativo na loja
              </label>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} data-testid="product-form-cancel">
                Cancelar
              </Button>
              <Button type="submit" disabled={saving} data-testid="product-form-save">
                {saving ? "Salvando..." : "Salvar Produto"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* confirmar exclusão */}
      <AlertDialog open={!!deleting} onOpenChange={() => setDeleting(null)}>
        <AlertDialogContent data-testid="product-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir produto?</AlertDialogTitle>
            <AlertDialogDescription>
              "{deleting?.name}" será removido permanentemente da loja.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="product-delete-cancel">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-red-600 hover:bg-red-700" data-testid="product-delete-confirm">
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
